import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core'
import { provideRouter, RouteReuseStrategy } from '@angular/router'
import { ActionReducer, MetaReducer, provideState, provideStore, Store } from '@ngrx/store'
import { provideStoreDevtools } from '@ngrx/store-devtools'
import { provideEffects } from '@ngrx/effects'
import { NgbDatepickerModule, NgbModule } from '@ng-bootstrap/ng-bootstrap'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ToastrModule } from 'ngx-toastr'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations' // Import this
import { authFeature } from './store/auth/auth.state'
import { authEffects } from './store/auth/auth.effects'

import { routes } from './app.routes'
import { provideClientHydration, withEventReplay } from '@angular/platform-browser'
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http'
import { authInterceptor } from './interceptors/token.interceptor'
import { businessEffects } from './store/business/business.effects'
import { businessFeature } from './store/business/business.reducer'
import { businessOwnersEffects } from './store/business-owner/business-owner.effects'
import { businessOwnerFeature } from './store/business-owner/business-owner.reducers'
import { cityEffects } from './store/city/city.effects'
import { countryEffects } from './store/country/country.effects'
import { stateEffects } from './store/state/state.effects'
import { countryFeature } from './store/country/country.reducer'
import { stateFeature } from './store/state/state.reducer'
import { cityFeature } from './store/city/city.reducer'
import { userFeature } from './store/user/user.reducer'
import { userEffects } from './store/user/user.effects'
import { officeFeature } from './store/office/office.reducer'
import { officeEffects } from './store/office/office.effects'
import { departmentFeature } from './store/department/department.reducer'
import { departmentEffects } from './store/department/department.effects'
import { designationFeature } from './store/designation/designation.reducer'
import { designationEffects } from './store/designation/designation.effects'
import { familyEffects } from './store/family/family.effects'
import { familyFeature } from './store/family/family.reducer'
import { bankEffects } from './store/bank/bank.effects'
import { bankFeature } from './store/bank/bank.reducer'
import { educationFeature } from './store/education/education.reducer'
import { educationEffects } from './store/education/education.effects'
import { experienceFeature } from './store/experience/experience.reducer'
import { experienceEffects } from './store/experience/experience.effects'
// import { roleFeature } from './store/role-permission/role-permission.reducer'
// import { roleEffects } from './store/role-permission/role-permission.effects'
import { employeeSalaryFeature } from './store/employee-salary/employee-salary.reducers'
import { employeeSalaryEffects } from './store/employee-salary/employee-salary.effects'
import { appliedLeaveFeature } from './store/applied-leave/applied-leave.reducer'
import { appliedLeaveEffects } from './store/applied-leave/applied-leave.effects'
import { provideDaterangepickerLocale } from 'ngx-daterangepicker-bootstrap'
import { clockInClockOutFeature } from './store/clockIn-clockOut/clockIn-clockOut.reducer'
import { clockInClockOutEffects } from './store/clockIn-clockOut/clockIn-clockOut.effects'
import { assignedPermissionSetsFeature } from './store/assigned-permissionsets/assigned-permissionsets.reducer'
import { assignedPermissionSetsEffects } from './store/assigned-permissionsets/assigned-permissionsets.effects'
import { SharedModule } from './shared/shared.module'
import { NgSelectModule } from '@ng-select/ng-select'
import { taskLogFeature } from './store/task-log/task-log.reducer'
import { taskLogsEffects } from './store/task-log/task-log.effects'
import { payrollFeature } from './store/payroll/payroll.reducers'
import { payrollEffects } from './store/payroll/payroll.effects'
import { employeeRequestFeature } from './store/employee-request/employee-request.reducer'
import { employeeRequestEffects } from './store/employee-request/employee-request.effects'
import { leavePolicyFeature } from './store/leave-policy/leave-policy.reducer'
import { leavePolicyEffects } from './store/leave-policy/leave-policy.effects'
import { environment } from '../environments/environment'
import { provideFirebaseApp, initializeApp } from '@angular/fire/app'
import { provideMessaging, getMessaging } from '@angular/fire/messaging'
import { provideServiceWorker } from '@angular/service-worker'
import { taxSettingsFeature } from './store/tax-settings/tax-settings.reducers'
import { taxSettingsEffects } from './store/tax-settings/tax-settings.effects'
import { payoutAgreementFeature } from './store/payout-agreement/payout-agreement.reducers'
import { payoutAgreementEffects } from './store/payout-agreement/payout-agreement.effects'
import { contractorPayrollEffects } from './store/contractor-payroll/contractor-payroll.effects'
import { contractorPayrollFeature } from './store/contractor-payroll/contractor-payroll.reducers'
import { roleEffects } from './store/role/role.effects'
import { roleFeature } from './store/role/role.reducer'
import { permissionSetEffects } from './store/permission-sets/permission-sets.effects'
import { permissionSetFeature } from './store/permission-sets/permission-sets.reducer'
import { TrimInputDirective } from './directives/trim-input.directive'
import { DisableRouteReuseStrategy } from './disable-route-reuse.strategy'
import { salaryComponentFeature } from './store/salary-components/salary-components.reducers'
import { salaryComponentsEffects } from './store/salary-components/salary-components.effects'
import { couponEffects } from './store/coupon/coupon.effects'
import { couponFeature } from './store/coupon/coupon.reducer'
import { AuthService } from './services/auth.service'
import { loginHistoryFeature } from './store/login-history/login-history.reducer'
import { loginHistoryEffects } from './store/login-history/login-history.effects'
// import { reducers } from './store'

// export function actionLogger(reducer: ActionReducer<any>): ActionReducer<any> {
//   return (state, action) => {
//     // console.log('%c[ACTION DISPATCHED]', 'color: red; font-size:14px', action)
//     return reducer(state, action)
//   }
// }

// const metaReducers: MetaReducer[] = isDevMode() ? [actionLogger] : []
// const metaReducers: MetaReducer[] = [actionLogger]

export function initAuth(authService: AuthService) {
  return () => authService.loadFromStorage()
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    { provide: RouteReuseStrategy, useClass: DisableRouteReuseStrategy },
    provideClientHydration(withEventReplay()),
    provideHttpClient(withInterceptors([authInterceptor])),
    importProvidersFrom(ReactiveFormsModule),
    importProvidersFrom(FormsModule),
    importProvidersFrom(NgbModule),
    importProvidersFrom(BrowserModule),
    importProvidersFrom(NgSelectModule),
    importProvidersFrom(NgbDatepickerModule),
    importProvidersFrom(BrowserAnimationsModule),
    importProvidersFrom(
      ToastrModule.forRoot({
        positionClass: 'toast-top-center', // Change to 'toast-top-center' or 'toast-top-left' as needed
        preventDuplicates: true, // Prevent duplicate messages
        timeOut: 6000, // Duration for which the toast will be visible
        progressBar: true // Show progress bar
      })
    ),

    //Provide state and effects here
    provideStore({}), // {} can be replaced with a global store file

    // provideStore(reducers, {
    //   metaReducers,
    //   // Optional, helpful in dev:
    //   runtimeChecks: {
    //     strictStateImmutability: true,
    //     strictActionImmutability: true,
    //     strictStateSerializability: false,
    //     strictActionSerializability: false
    //   }
    // }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
      connectInZone: true
    }),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
    provideState(authFeature),
    provideEffects(authEffects),
    provideState(businessOwnerFeature),
    provideEffects(businessOwnersEffects),
    provideState(countryFeature),
    provideEffects(countryEffects),
    provideState(stateFeature),
    provideEffects(stateEffects),
    provideState(cityFeature),
    provideEffects(cityEffects),
    provideState(userFeature),
    provideEffects(userEffects),
    provideState(salaryComponentFeature),
    provideEffects(salaryComponentsEffects),
    provideState(businessFeature),
    provideEffects(businessEffects),
    provideState(officeFeature),
    provideEffects(officeEffects),
    provideState(departmentFeature),
    provideEffects(departmentEffects),
    provideState(designationFeature),
    provideEffects(designationEffects),
    provideState(familyFeature), // reducer
    provideEffects([familyEffects]), // effect
    provideState(bankFeature), // reducer
    provideEffects([bankEffects]), // effect
    provideState(educationFeature), // reducer
    provideEffects([educationEffects]), // effect
    provideState(experienceFeature), // reducer
    provideEffects([experienceEffects]), // effect
    // provideState(roleFeature),
    // provideEffects(roleEffects),
    provideState(employeeSalaryFeature),
    provideEffects(employeeSalaryEffects),
    provideState(payoutAgreementFeature),
    provideEffects(payoutAgreementEffects),
    provideState(appliedLeaveFeature),
    provideEffects(appliedLeaveEffects),
    provideDaterangepickerLocale({
      separator: ' - ',
      applyLabel: 'Okay'
    }),
    provideState(clockInClockOutFeature),
    provideEffects(clockInClockOutEffects),
    provideState(assignedPermissionSetsFeature),
    provideEffects(assignedPermissionSetsEffects),
    importProvidersFrom(SharedModule),
    provideState(taskLogFeature),
    provideEffects(taskLogsEffects),
    provideState(payrollFeature),
    provideEffects(payrollEffects),
    provideState(contractorPayrollFeature),
    provideEffects(contractorPayrollEffects),
    provideState(employeeRequestFeature),
    provideEffects(employeeRequestEffects),
    provideState(leavePolicyFeature),
    provideEffects(leavePolicyEffects),
    provideState(taxSettingsFeature),
    provideEffects(taxSettingsEffects),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideState(roleFeature),
    provideEffects(roleEffects),
    provideState(permissionSetFeature),
    provideEffects(permissionSetEffects),
    provideState(couponFeature),
    provideEffects(couponEffects),
    importProvidersFrom(TrimInputDirective),
    provideState(loginHistoryFeature),
    provideEffects(loginHistoryEffects)
  ]
}
